import {
  Alert,
  AlertIcon,
  Button,
  HStack,
  Stack,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  ExpenseStatusEnum,
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
} from "@src/__generated__/graphql";
import { DuplicateExpensesBanner } from "@src/components/modules/expenses/Create/components/Header/DuplicateExpensesBanner";
import { useFetchDuplicateExpenses } from "@src/components/modules/expenses/Create/hooks";
import { ModalConfirm } from "@src/components/ui-kit";
import { routeHistory } from "@src/helpers/route-history";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { useStore as useAppStore } from "@src/utils/hooks";
import { getQueryParamOptional } from "@src/utils/routing";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import { useCallback } from "react";
import { useStore } from "../../store";
import { ExpenseUpdateWhileInactiveBanner } from "../Header";
import { Save } from "./Save";

export const Footer = observer(function Footer() {
  const store = useStore();
  const appStore = useAppStore();
  const { expense, form, setScrollTo } = useStore();
  const router = useRouter();
  const isFinancier = can("expense_approve");
  const preventSaveDueToDuplicatedLabel = t`Cannot save because this expense has duplicates.`;

  const expenseIds = useFetchDuplicateExpenses({
    contractorId: form.contractor_id.$,
    variableSymbol: form.variable_symbol.$,
    excludeExpenseId: expense?.id,
    documentNumber: form.document_number.$,
  });

  const preventSaveDueToDuplicates =
    !!appStore.workspaceStore.settings?.preventSavingDuplicatedExpenses &&
    expenseIds.length > 0;

  const showButton = () => {
    if (isFinancier) {
      return {
        draft: true,
        request_approval: !store.everyItemApproved,
        save: store.everyItemApproved,
        save_approved: true,
      };
    }
    switch (expense?.status) {
      case ExpenseStatusEnum.ForApproval:
        return {
          draft: false,
          request_approval: !store.everyItemApproved,
          save: store.everyItemApproved,
          save_approved: false,
        };
      case ExpenseStatusEnum.ForFinancialApproval:
        return {
          draft: false,
          request_approval: false,
          save: false,
          save_approved: false,
        };
      case ExpenseStatusEnum.Approved:
        return {
          draft: false,
          request_approval: false,
          save: false,
          save_approved: false,
        };
      case ExpenseStatusEnum.Draft:
      default:
        return {
          draft: true,
          request_approval: !store.everyItemApproved,
          save: store.everyItemApproved,
          save_approved: false,
        };
    }
  };

  const onSuccessSave = useCallback(() => {
    const projectId = getQueryParamOptional("projectId", router.query);
    if (projectId) {
      router.push({ pathname: "/projects/detail", query: { id: projectId } });
    } else {
      window.history.length > 1
        ? router.back()
        : router.push(routeHistory.get("/expenses"));
    }
  }, []);

  const [create, { loading: createLoading }] = useCreateExpenseMutation({
    onCompleted() {
      appStore.UIStore.toast({
        title: t`The expense was created.`,
        status: "success",
      });
      onSuccessSave();
    },
    ...onError(),
  });

  const [update, { loading: updateLoading }] = useUpdateExpenseMutation({
    onCompleted() {
      appStore.UIStore.toast({
        title: t`The expense was updated.`,
        status: "success",
      });
      onSuccessSave();
    },
    ...onError(),
  });

  const handleDeleteExpense = async () => {
    const success = await store.deleteExpense();
    if (!success) return;
    onSuccessSave();
  };

  const validateAndSave = async (isDraft: boolean, isRequest?: boolean) => {
    const { hasError } = await form.validateAsDraft();

    if (hasError) {
      return setScrollTo();
    }

    save(isDraft, isRequest ?? false);
  };

  const save = (is_draft: boolean, for_approval: boolean) => {
    if (expense?.id) {
      update({
        variables: { input: store.inputUpdate({ is_draft, for_approval }) },
      });
    } else {
      create({
        variables: { input: store.inputCreate({ is_draft, for_approval }) },
      });
    }
  };

  return (
    <>
      <VStack align="end" w="full" mt="8" spacing="2">
        <HStack
          justify="end"
          mb={
            expenseIds.length || store.expenseUpdatedWhileInactive
              ? "6"
              : undefined
          }
          spacing="6"
        >
          {!!expenseIds.length && (
            <DuplicateExpensesBanner expenseIds={expenseIds} />
          )}

          <ExpenseUpdateWhileInactiveBanner />
        </HStack>

        <Stack
          flexDir={{
            base: "column",
            md: "row",
          }}
          w="full"
          spacing="2"
        >
          {store.canDeleteExpense && (
            <Button
              mb={{
                base: "6",
                md: "0",
              }}
              colorScheme="red"
              isDisabled={
                store.isDocumentLocked ||
                store.expenseUpdatedWhileInactive ||
                store.hasModelLock
              }
              isLoading={store.isDeleting}
              onClick={store.deleteExpenseModalState.onOpen}
              variant="outline"
            >
              <Trans>Delete</Trans>
            </Button>
          )}

          <Stack
            justify="end"
            flexDir={{
              base: "column",
              md: "row",
            }}
            w="full"
            spacing="2"
          >
            {showButton().draft && (
              <Tooltip
                isDisabled={!preventSaveDueToDuplicates}
                label={preventSaveDueToDuplicatedLabel}
              >
                <Button
                  isDisabled={
                    store.isDocumentLocked ||
                    store.expenseUpdatedWhileInactive ||
                    store.hasModelLock ||
                    preventSaveDueToDuplicates
                  }
                  isLoading={createLoading || updateLoading}
                  onClick={() => {
                    validateAndSave(true);
                  }}
                  variant="subtle"
                >
                  <Trans>Save as draft</Trans>
                </Button>
              </Tooltip>
            )}
            {showButton().request_approval && (
              <Tooltip
                isDisabled={!preventSaveDueToDuplicates}
                label={preventSaveDueToDuplicatedLabel}
              >
                <Button
                  isDisabled={
                    store.isDocumentLocked ||
                    store.expenseUpdatedWhileInactive ||
                    store.hasModelLock ||
                    preventSaveDueToDuplicates
                  }
                  isLoading={createLoading || updateLoading}
                  onClick={() => {
                    validateAndSave(true, true);
                  }}
                  variant="subtle"
                >
                  <Trans>Request items approval</Trans>
                </Button>
              </Tooltip>
            )}
            {showButton().save && (
              <Tooltip
                isDisabled={!preventSaveDueToDuplicates}
                label={preventSaveDueToDuplicatedLabel}
              >
                <Button
                  isDisabled={
                    store.isDocumentLocked ||
                    store.expenseUpdatedWhileInactive ||
                    store.hasModelLock ||
                    preventSaveDueToDuplicates
                  }
                  isLoading={createLoading || updateLoading}
                  onClick={() => {
                    validateAndSave(true, true);
                  }}
                  variant={isFinancier ? "subtle" : "solid"}
                >
                  {isFinancier ? (
                    <Trans>Save as For Financial Approval</Trans>
                  ) : (
                    <Trans>Save for Financial Approval</Trans>
                  )}
                </Button>
              </Tooltip>
            )}
            {showButton().save_approved && (
              <Save
                onSave={onSuccessSave}
                preventSaveDueToDuplicates={preventSaveDueToDuplicates}
              />
            )}
          </Stack>
        </Stack>
      </VStack>
      <ModalConfirm
        isOpen={store.deleteExpenseModalState.isOpen}
        onClose={store.deleteExpenseModalState.onClose}
        destructive
        cancelBtnTitle={t`Cancel`}
        confirmBtnTitle={t`Delete`}
        heading={t`Delete expense`}
        onConfirm={handleDeleteExpense}
      >
        <Trans>Are you sure you want to delete this expense?</Trans>
        {store.isLinkedToLockedTimeBilling && (
          <Alert mt="4" status="warning">
            <AlertIcon />
            <Trans>
              This expense contains items linked to a locked Time Billing
              calculation. Deleting this expense may affect the Time Billing.
            </Trans>
          </Alert>
        )}
      </ModalConfirm>
    </>
  );
});
